import { logTrace, logError } from './loggerService';
import { showErrorModal } from './errorModalService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const X_API_KEY = import.meta.env.VITE_X_API_KEY || '';

// State that persists as long as the user is logged in
let jwt: string | null = null;
let clerkJwt: string | null = null;
let activeProfileId: string | null = null;

// Token refresh callback - set by IsAuthenticated component
let tokenRefreshCallback: (() => Promise<void>) | null = null;

// Setters for authentication state
export function setJWT(token: string | null) {
  jwt = token;
}

export function setClerkJWT(token: string | null) {
  clerkJwt = token;
}

export function setActiveProfileId(profileId: string | null) {
  activeProfileId = profileId;
}

export function clearAuthState() {
  jwt = null;
  clerkJwt = null;
  activeProfileId = null;
}

// Getters for checking current state
export function getJWT(): string | null {
  return jwt;
}

export function getClerkJWT(): string | null {
  return clerkJwt;
}

export function getActiveProfileId(): string | null {
  return activeProfileId;
}

export function setTokenRefreshCallback(callback: (() => Promise<void>) | null) {
  tokenRefreshCallback = callback;
}

// Build headers for API requests
function buildHeaders(includeContentType = true, serviceKey?: string): HeadersInit {
  const headers: Record<string, string> = {};

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  // If serviceKey is provided, use it instead of JWT
  if (serviceKey) {
    headers['Authorization'] = `Bearer ${serviceKey}`;
  } else {
    // Use API JWT if available, otherwise fall back to Clerk JWT (for login-with-ext-jwt)
    const authToken = jwt || clerkJwt;
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
  }

  if (X_API_KEY) {
    headers['X-Api-Key'] = X_API_KEY;
  }

  if (activeProfileId) {
    headers['X-Sub-Identity'] = activeProfileId;
  }

  return headers;
}

// Generic request helper
async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false,
  serviceKey?: string
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const method = options.method || 'GET';

  const response = await fetch(url, {
    ...options,
    headers: {
      ...buildHeaders(true, serviceKey),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.text();

    const isLocal = import.meta.env.VITE_ENV === 'local';
    if (response.status === 401 && !isRetry && tokenRefreshCallback && isLocal) {
      try {
        if (errorBody.includes('jwt') || errorBody.includes('JWT')) {
          logTrace('401 JWT error detected, refreshing token and retrying...', undefined, {
            endpoint,
            status: 401,
          });
          await tokenRefreshCallback();
          return request<T>(endpoint, options, true, serviceKey);
        }
      } catch (refreshError) {
        logError(refreshError as Error, 'Token refresh failed', undefined, { endpoint });
      }
    }

    let correlationId: string | undefined;
    try {
      const errorJson = JSON.parse(errorBody);
      correlationId = errorJson.correlationId || errorJson.CorrelationId;
    } catch {
      // Not JSON or no correlationId
    }

    const error = new Error(`API Error ${response.status}: ${errorBody}`);
    logError(error, `${method} ${endpoint} failed`, undefined, {
      status: response.status,
      endpoint,
      body: errorBody,
      correlationId,
    });
    showErrorModal(error.message, correlationId);
    throw error;
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    logTrace(`${method} ${endpoint} completed`, undefined, { status: 204 });
    return undefined as T;
  }

  const data = await response.json();
  logTrace(`${method} ${endpoint} completed`, undefined, { status: response.status, data });
  return data;
}

// HTTP method helpers
export async function get<T>(endpoint: string, serviceKey?: string): Promise<T> {
  return request<T>(endpoint, { method: 'GET' }, false, serviceKey);
}

export async function getById<T>(endpoint: string, id: string, queryParams?: string, serviceKey?: string): Promise<T> {
  const url = queryParams ? `${endpoint}/${id}?${queryParams}` : `${endpoint}/${id}`;
  return request<T>(url, { method: 'GET' }, false, serviceKey);
}

export async function post<T>(endpoint: string, body: unknown, serviceKey?: string): Promise<T> {
  return request<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  }, false, serviceKey);
}

export async function put<T>(endpoint: string, body: unknown, serviceKey?: string): Promise<T> {
  return request<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  }, false, serviceKey);
}

export async function patch<T>(endpoint: string, body: unknown, serviceKey?: string): Promise<T> {
  return request<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }, false, serviceKey);
}

export async function del<T>(endpoint: string, serviceKey?: string): Promise<T> {
  return request<T>(endpoint, { method: 'DELETE' }, false, serviceKey);
}

export async function postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: buildHeaders(false),
    body: formData,
  });

  if (!response.ok) {
    const errorBody = await response.text();

    // Try to extract correlationId from the error response
    let correlationId: string | undefined;
    try {
      const errorJson = JSON.parse(errorBody);
      correlationId = errorJson.correlationId || errorJson.CorrelationId;
    } catch {
      // Not JSON or no correlationId, continue without it
    }

    const error = new Error(`API Error ${response.status}: ${errorBody}`);
    logError(error, `POST ${endpoint} failed`, undefined, {
      status: response.status,
      endpoint,
      body: errorBody,
      correlationId,
    });
    showErrorModal(error.message, correlationId);
    throw error;
  }

  if (response.status === 204) {
    logTrace(`POST ${endpoint} completed`, undefined, { status: 204 });
    return undefined as T;
  }

  const data = await response.json();
  logTrace(`POST ${endpoint} completed`, undefined, { status: response.status, data });
  return data;
}

// Convenience export for all methods
export const apiService = {
  get,
  getById,
  post,
  postFormData,
  put,
  patch,
  delete: del,
  setJWT,
  setClerkJWT,
  setActiveProfileId,
  setTokenRefreshCallback,
  clearAuthState,
  getJWT,
  getClerkJWT,
  getActiveProfileId,
};
